import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS - Apple-Level Precision
// ═══════════════════════════════════════════════════════════════════════════════
const ease = [0.16, 1, 0.3, 1]; // Expo out - Apple's signature curve
const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };

// ═══════════════════════════════════════════════════════════════════════════════
// ANIMATED COMPONENTS - Purposeful Motion
// ═══════════════════════════════════════════════════════════════════════════════
function AnimatedHeadline({ children, className = "", delay = 0 }) {
  return (
    <motion.h1
      className={className}
      initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 1.2, delay, ease }}
    >
      {children}
    </motion.h1>
  );
}

function FadeInSection({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 1, delay, ease }}
    >
      {children}
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HOMEPAGE - Peak UX Design
// ═══════════════════════════════════════════════════════════════════════════════
export default function HomePage() {
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  
  // Smooth parallax with spring physics
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const smoothProgress = useSpring(scrollYProgress, springConfig);
  
  const heroOpacity = useTransform(smoothProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(smoothProgress, [0, 1], [1, 1.1]);
  const heroY = useTransform(smoothProgress, [0, 1], [0, 150]);
  const textY = useTransform(smoothProgress, [0, 0.5], [0, -50]);

  const scrollToStats = () => statsRef.current?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div data-testid="home-page" className="bg-[#0A1628]">
      
      {/* ═══════════════════════════════════════════════════════════════════════
          HERO - Cinematic, Full-Bleed, One Action
      ═══════════════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-screen w-full overflow-hidden">
        {/* Full-bleed background with parallax */}
        <motion.div 
          className="absolute inset-0 w-full h-full" 
          style={{ scale: heroScale, y: heroY }}
        >
          <img
            src="https://static.prod-images.emergentagent.com/jobs/3f9d48bb-c43d-49fc-a95e-a7e242b10eb2/images/9ed990cee83c25ed93a0faeaac59955f048d6a6eae5296931bda44dcd377582b.png"
            alt="Newborn baby"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          
          {/* Darker, more intentional overlay system */}
          <div className="absolute inset-0">
            {/* Base dark overlay - stronger */}
            <div className="absolute inset-0 bg-[#0A1628]/75" />
            {/* Vignette for depth */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_60%_at_50%_50%,transparent_0%,#0A1628_100%)] opacity-60" />
            {/* Top fade - seamless with banner */}
            <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#0A1628] via-[#0A1628]/90 to-transparent" />
            {/* Bottom fade - seamless transition */}
            <div className="absolute inset-x-0 bottom-0 h-60 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/80 to-transparent" />
          </div>
        </motion.div>

        {/* Content with parallax text */}
        <div className="relative z-20 h-full flex flex-col items-center justify-center px-6">
          <motion.div 
            className="text-center max-w-4xl"
            style={{ opacity: heroOpacity, y: textY }}
          >
            <AnimatedHeadline delay={0.3}>
              <span className="block text-[28px] sm:text-[36px] md:text-[44px] lg:text-[52px] xl:text-[58px] font-heading font-medium text-white/85 tracking-[-0.01em] leading-[1.15]">
                Every Birth Should Be a
              </span>
              <span className="block text-[36px] sm:text-[48px] md:text-[60px] lg:text-[72px] xl:text-[80px] font-heading font-semibold text-[#E8A33D] tracking-[-0.02em] leading-[1.1] mt-2">
                Healthy Birth
              </span>
            </AnimatedHeadline>

            {/* Single CTA - scroll prompt */}
            <motion.div
              className="mt-16 md:mt-24"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.5 }}
            >
              <button
                onClick={scrollToStats}
                className="group inline-flex flex-col items-center gap-3 text-white/40 hover:text-white/70 transition-colors duration-500"
                data-testid="discover-why-btn"
              >
                <span className="text-[10px] tracking-[0.3em] uppercase font-medium">Discover Why</span>
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <ArrowDown size={16} strokeWidth={1} />
                </motion.div>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          THE SCALE - Scroll-Linked Storytelling
      ═══════════════════════════════════════════════════════════════════════ */}
      <section ref={statsRef} className="bg-[#0A1628] py-40 md:py-56">
        <div className="max-w-5xl mx-auto px-6">
          <FadeInSection className="text-center mb-32">
            <p className="text-[#E8A33D] text-[10px] tracking-[0.4em] uppercase mb-16 font-medium">
              The Scale of the Challenge
            </p>
            
            <div className="mb-10">
              <motion.span 
                className="text-[100px] md:text-[160px] lg:text-[200px] font-heading font-extralight text-white leading-none tracking-[-0.04em] inline-block"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, ease }}
              >
                30
              </motion.span>
              <motion.span 
                className="text-3xl md:text-5xl font-heading font-light text-[#E8A33D] ml-2 tracking-tight"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.3, ease }}
              >
                M
              </motion.span>
            </div>
            
            <p className="text-white/30 text-base md:text-lg max-w-2xl mx-auto leading-relaxed font-light">
              Number of babies requiring specialized newborn care annually due to being born too small, too soon, or with complications in the first 28 days of life
            </p>
          </FadeInSection>

          {/* Supporting stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-8 max-w-4xl mx-auto">
            {[
              { value: "20M", desc: "Babies born with low birthweight each year" },
              { value: "20×", desc: "Higher mortality risk in the neonatal period" },
              { value: "90%", desc: "Born in low- and middle-income countries" },
            ].map((stat, i) => (
              <FadeInSection key={stat.value} delay={i * 0.15} className="text-center">
                <p className="text-3xl md:text-4xl font-heading font-extralight text-white mb-4 tracking-tight">
                  {stat.value}
                </p>
                <p className="text-white/20 text-sm leading-relaxed max-w-[200px] mx-auto">
                  {stat.desc}
                </p>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          LIFE COURSE IMPACT - One Focus Per View
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-[#F8F9FA] py-40 md:py-56">
        <div className="max-w-5xl mx-auto px-6">
          <FadeInSection className="mb-24">
            <p className="text-[#E8A33D] text-[10px] tracking-[0.4em] uppercase mb-8 font-medium">
              Understanding Birth Outcomes
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-[44px] text-[#0A1628] font-semibold tracking-[-0.02em] leading-[1.15] max-w-3xl">
              Birth outcomes shape health and human potential across the life course
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-20 gap-y-16">
            {[
              { stat: "20×", title: "Neonatal Mortality", desc: "Up to twenty times higher risk of death in the first month of life for low birthweight babies." },
              { stat: "Higher", title: "Childhood Stunting", desc: "Children born small have substantially higher risk of stunting and impaired physical growth." },
              { stat: "Long-term", title: "Cognitive Impact", desc: "Associated with poorer cognitive development, lower educational attainment, and behavioral challenges." },
              { stat: "40%+", title: "Chronic Disease", desc: "Linked to higher risk of hypertension, cardiovascular disease, and type 2 diabetes in adulthood." },
            ].map((item, i) => (
              <FadeInSection key={item.title} delay={i * 0.1}>
                <div className="group">
                  <p className="text-[#E8A33D] text-2xl font-heading font-light mb-3 tracking-tight">{item.stat}</p>
                  <h3 className="text-[#0A1628] text-lg font-semibold mb-3 tracking-tight">{item.title}</h3>
                  <p className="text-[#0A1628]/40 leading-relaxed text-[15px]">{item.desc}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          THE POSSIBILITY - Centered Focus
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-[#F8F9FA] py-40 md:py-56">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeInSection>
            <p className="text-[#E8A33D] text-[10px] tracking-[0.4em] uppercase mb-12 font-medium">
              The Possibility
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-[44px] text-[#0A1628] font-semibold tracking-[-0.02em] leading-[1.2] mb-10 max-w-3xl mx-auto">
              Prioritized action in the highest-burden countries can reduce the global burden of low birth weight and transform outcomes for small and sick newborns.
            </h2>
            <p className="text-xl md:text-2xl text-[#0A1628]/45 font-light tracking-tight">
              We can <span className="text-[#E8A33D] font-medium">save millions of newborns</span> within a decade.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          FOUR ENGINES - Spatial Luxury
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-white py-40 md:py-56">
        <div className="max-w-6xl mx-auto px-6">
          <FadeInSection className="text-center mb-24">
            <p className="text-[#E8A33D] text-[10px] tracking-[0.4em] uppercase mb-8 font-medium">
              Our Framework for Action
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-[44px] text-[#0A1628] font-semibold tracking-[-0.02em]">
              Four Engines Working for One Mission
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { num: "01", title: "Impact", tagline: "National ambition backed by system readiness and technical leadership.", desc: "Support national governments in setting and delivering audacious goals through system readiness, introduction and scaling of innovations, and provision of technical assistance across their highest-burden geographies.", color: "#1E3A5F" },
              { num: "02", title: "Innovation", tagline: "Innovations attuned for impact within real-world health systems at real scale.", desc: "Introduce, integrate and scale at least 20 scale-ready innovations into routine care across national health systems.", color: "#2B7D75" },
              { num: "03", title: "Investment", tagline: "Catalytic capital aligned to country ambition and sustained through domestic financing.", desc: "Mobilize and align catalytic, public, and philanthropic financing behind country-defined goals.", color: "#1E3650" },
              { num: "04", title: "Insight & Influence", tagline: "Insights to inspire and influence more action, regionally and globally.", desc: "A global plug-and-play insight to influence platform curating evidence, generating learning from country programs, and channeling it for policymaking and investment decisions.", color: "#22606F" },
            ].map((engine, i) => (
              <FadeInSection key={engine.title} delay={i * 0.1}>
                <motion.div
                  className="p-10 md:p-12 rounded-3xl h-full flex flex-col relative overflow-hidden"
                  style={{ backgroundColor: engine.color }}
                  whileHover={{ scale: 1.02, y: -4 }}
                  transition={{ duration: 0.4, ease }}
                  data-testid={`engine-card-${engine.num}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] to-transparent pointer-events-none" />
                  
                  <p className="text-white/15 text-[10px] tracking-[0.2em] mb-8 relative">{engine.num}</p>
                  <h3 className="text-white text-xl md:text-2xl font-semibold mb-3 tracking-tight relative">{engine.title}</h3>
                  <p className="text-white/50 text-sm italic mb-6 leading-relaxed relative">{engine.tagline}</p>
                  <p className="text-white/25 text-sm leading-relaxed mt-auto relative">{engine.desc}</p>
                </motion.div>
              </FadeInSection>
            ))}
          </div>

          <FadeInSection delay={0.4} className="text-center mt-20">
            <Link to="/framework">
              <motion.button 
                className="inline-flex items-center gap-3 bg-[#0A1628] text-white px-10 py-5 rounded-full text-sm font-medium tracking-wide"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.3 }}
                data-testid="explore-framework-btn"
              >
                Explore Our Framework
                <ArrowRight size={15} strokeWidth={1.5} />
              </motion.button>
            </Link>
          </FadeInSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          HIGH RETURN - Dark Section, Single Focus
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-[#0A1628] py-40 md:py-56">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeInSection>
            <p className="text-[#E8A33D] text-[10px] tracking-[0.4em] uppercase mb-12 font-medium">
              A High Return Investment
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-[44px] text-white font-semibold tracking-[-0.02em] leading-[1.2] mb-8">
              Investing in LBW and SSNB is one of the highest-return investments in global health
            </h2>
            <p className="text-white/30 text-base md:text-lg mb-24 max-w-2xl mx-auto leading-relaxed">
              Every US$1 invested in quality care for small and sick newborns yields US$8–13 in economic and social returns.
            </p>
          </FadeInSection>
          
          <div className="flex flex-col md:flex-row justify-center items-center gap-20 md:gap-28 mb-24">
            <FadeInSection delay={0.2} className="text-center">
              <p className="text-4xl md:text-5xl font-heading font-extralight text-white mb-3 tracking-tight">17 years</p>
              <p className="text-white/20 text-sm">Average time for research to reach practice</p>
            </FadeInSection>
            <div className="hidden md:block w-px h-20 bg-white/10" />
            <FadeInSection delay={0.3} className="text-center">
              <p className="text-4xl md:text-5xl font-heading font-extralight text-white mb-3 tracking-tight">64 countries</p>
              <p className="text-white/20 text-sm">Off track for SDG neonatal mortality goal</p>
            </FadeInSection>
          </div>

          <FadeInSection delay={0.4}>
            <Link to="/join">
              <motion.button 
                className="inline-flex items-center gap-3 bg-white text-[#0A1628] px-10 py-5 rounded-full text-sm font-medium tracking-wide"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.3 }}
                data-testid="join-initiative-btn"
              >
                Join the Initiative
                <ArrowRight size={15} strokeWidth={1.5} />
              </motion.button>
            </Link>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
}
